import React from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { useAuth } from '@/context/AuthContext';
import { Sidebar } from '@/components/Sidebar';
import { PageRenderer } from '@/components/PageRenderer';
import { AlliesDialog } from '@/components/AlliesDialog';
import { Button } from '@/components/ui/button';
import { Menu, X, Moon, Sun, Download, RotateCcw, LogOut, FileDown } from 'lucide-react';

function WorkbookLayout() {
  var workbook = useWorkbook();
  var auth = useAuth();
  
  var sidebarOpen = workbook.sidebarOpen;
  var setSidebarOpen = workbook.setSidebarOpen;
  var darkMode = workbook.darkMode;
  var setDarkMode = workbook.setDarkMode;
  var exportData = workbook.exportData;
  var resetWorkbook = workbook.resetWorkbook;
  var calculateProgress = workbook.calculateProgress;
  
  var progress = calculateProgress();
  
  function handlePrintPDF() {
    window.print();
  }
  
  function handleLogout() {
    if (window.confirm('Are you sure you want to sign out?')) {
      auth.logout();
    }
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between no-print">
        <Button
          variant="ghost"
          size="icon"
          onClick={function() { setSidebarOpen(!sidebarOpen); }}
          className="shrink-0"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        
        <h1 className="font-serif text-sm font-bold truncate px-2">The Rules of Money</h1>
        
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={function() { setDarkMode(!darkMode); }}
            className="shrink-0"
          >
            {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </header>
      
      {/* Sidebar */}
      <div className="no-print">
        <Sidebar />
      </div>
      
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-foreground/20 z-30 no-print"
          onClick={function() { setSidebarOpen(false); }}
        />
      )}
      
      {/* Main content */}
      <main 
        className={'min-h-screen transition-all duration-300 pt-16 lg:pt-0 ' + (sidebarOpen ? 'lg:ml-72' : 'lg:ml-0')}
      >
        {/* Desktop header bar */}
        <div 
          className="hidden lg:flex fixed top-0 right-0 left-0 z-20 bg-card/80 backdrop-blur-sm border-b border-border px-6 py-3 items-center justify-between no-print"
          style={{ marginLeft: sidebarOpen ? '18rem' : '0' }}
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={function() { setSidebarOpen(!sidebarOpen); }}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Progress:</span>
              <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: progress + '%' }}
                />
              </div>
              <span className="text-sm font-medium">{progress}%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <AlliesDialog />
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrintPDF}
              className="gap-2"
              title="Print or save as PDF"
            >
              <FileDown className="h-4 w-4" />
              <span className="hidden xl:inline">PDF</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={exportData}
              className="gap-2"
              title="Export your data"
            >
              <Download className="h-4 w-4" />
              <span className="hidden xl:inline">Export</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetWorkbook}
              className="gap-2 text-destructive hover:text-destructive"
              title="Reset workbook"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="hidden xl:inline">Reset</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={function() { setDarkMode(!darkMode); }}
              title="Toggle dark mode"
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2"
              title="Sign out"
              data-testid="sign-out-button"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden xl:inline">Sign Out</span>
            </Button>
          </div>
        </div>
        
        {/* Page content */}
        <div className="lg:pt-16">
          <PageRenderer />
        </div>
      </main>
    </div>
  );
}

export { WorkbookLayout };
